
import React, { useEffect, useState } from 'react';
import { Box, styled, Typography } from '@mui/material';
import GenericDraggableTableView from '../../../../Features/Table/GenericDraggableTable';
import CartLoader from '../../../../Helper/Loaders/CartLoader';
import { useTranslation } from 'react-i18next';
import { getColumnsConfig } from '../../../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations';
import { usePagesContent } from '../../../../Hooks/usePagesContent';
import { IDDisplayBox } from '../../../../Theme/DigitalTwinStyles';
import { formatSocValue } from '../../../../Helper/DigitalTwin/DigitalTwinHelper';
import { getBatteriesHealth } from '../../../../Api/BatteryHealthApi';
import { getNodesAtHierarchyLevel } from '../../../../Helper/DigitalTwin/DigitalTwinFunctions';

const TableContainer = styled(Box)(({ theme }) => ({
    marginTop: '20px',
    width: '96.5%',
    minHeight: '200px',
    '& .ant-table-cell-with-append': {
        display: 'flex',
        alignItems: 'center'
    }
}))

const customRenderers = {
    live_name: (record, config) => {
        const indent = record.hierarchyLevel == 'cell' ? 35 : 0;
        return (
            <div style={{ paddingLeft: indent }}>
                <Typography variant="fw400s14lh24c0061CA" sx={{ "color": "#3D4B5A !important" }}>
                    {record[config.dataIndex]}
                </Typography>
            </div>
        );
    },

    'live_soc': (record, config) => {
        let soc = record[config.dataIndex]
        return (<Box sx={{ textAlign: 'right', color: soc && soc < 20 ? '#B50500' : '' }}>{formatSocValue(soc)}</Box>)
    }
};

const columns = {
    'name': {
        title: 'Battery System',
        dataIndex: 'name',
        key: 'name',
        fixed: 'left',
        valueRenderingType: 'live_name'
    },
    'unitID': {
        title: 'Unit ID ',
        dataIndex: 'unitID',
        key: 'unitID',
        fixed: 'left',
        valueRenderingType: 'text-with-copy'
    },

    'soh': {
        title: 'SOH (%)',
        dataIndex: 'soh',
        key: 'soh',
        decimalPlaces: 0,
        valueRenderingType: 'number',
    },
    'sohConfidence': {
        title: 'SOH Estimation Confidence',
        dataIndex: 'sohConfidence',
        key: 'sohConfidence',
        defaultValue: '-',
        valueRenderingType: 'text',
        textFormat: 'title',
    },
    'age': {
        title: 'Age (month)',
        dataIndex: 'age',
        key: 'age',
        decimalPlaces: 0,
        valueRenderingType: 'age'
    },

    'rul': {
        title: 'RUL (Cycles)',
        dataIndex: 'rul',
        key: 'rul',
        decimalPlaces: 0,
        valueRenderingType: 'number'
    },

    'rul_age': {
        title: 'RUL (months)',
        dataIndex: 'rul_age',
        key: 'rul_age',
        decimalPlaces: 0,
        valueRenderingType: 'age'
    },

    // 'degradation_rate': {
    //     title: 'Degradation Rate (% per 100 cycles)',
    //     dataIndex: 'degradation_rate',
    //     key: 'degradation_rate',
    //     decimalPlaces: 0,
    //     valueRenderingType: 'number'
    // },

    'initial_capacity': {
        title: 'Initial Capacity (Ah)',
        dataIndex: 'initial_capacity',
        key: 'initial_capacity',
        decimalPlaces: 0,
        valueRenderingType: 'number'
    },
    'present_capacity': {
        title: 'Present Capacity (Ah)',
        dataIndex: 'present_capacity',
        decimalPlaces: 0,
        key: 'present_capacity',
        valueRenderingType: 'number'
    },
}


const enrichBatteryStructure = (nodes, level = 0) => {
    return nodes.map((node, index) => {
        const info = node.info || {}
        const enriched = {
            key: node.id,
            name: node.label,
            unitID: node.displayId,
            soh: info.sohEst || 0,
            sohConfidence: info.sohConfidence ?? null,
            age: info.age || 0,
            rul: info.rulCycles || 0,
            rul_age: info.rulAge || 0,
            initial_capacity: info.initialCapacity || 0,
            present_capacity: info.presentCapacity || 0,
            level,
        };
        return enriched;
    });
};

const getColumnOrder = (columns, orgMetrics) => {
    let allMetrics = Object.keys(columns)
    if (!orgMetrics || orgMetrics.length === 0) {
        return allMetrics
    }
    orgMetrics.push('name', 'unitID', 'lastPinged')

    return allMetrics.filter(key => orgMetrics.includes(key))
}

const BatteryLifecycleMetrics = ({ batteryStructure, batteryInfo }) => {
    const { t } = useTranslation(['common', 'generic_table'])
    const pageContent = usePagesContent()
    const [tableData, setTableData] = useState([])
    const tableColumns = getColumnsConfig({
        tableColumns: columns,
        tableColumnOrder: getColumnOrder(columns, []),
        customRenderers,
        t
    })


    const setupTableData = async () => {
        let batteryPacks = getNodesAtHierarchyLevel(batteryStructure, 'pack')
        let batteryPacksIds = batteryPacks.map(item => item.id)
        const res = await getBatteriesHealth({ "packConfigID": batteryPacksIds })

        const responseData = res.response.data || []

        const sohData = batteryPacks.map(item => {
            const packData = responseData.find(data => data.packConfigID === item.id)
            return {
                ...item,
                info: { ...item.info, ...packData },
            }
        })

        let tableData = enrichBatteryStructure(sohData)
        setTableData(tableData)
    }

    useEffect(() => {
        setupTableData()
    }, [batteryStructure])


    if (!batteryStructure || batteryStructure.length === 0) {
        return <CartLoader />
    }


    // let tableData = enrichBatteryStructure(batteryStructure)

    const customRightActionsView = (
        <IDDisplayBox>
            <Box>
                <Typography variant="fw400s12c494949">{t(`common:device_id`)}: </Typography>
                <Typography variant="fw400s12c494949">{batteryInfo.deviceID}</Typography>
            </Box>
        </IDDisplayBox>
    )

    return (
        <TableContainer>
            <GenericDraggableTableView
                tableID={'InstantaneousMatrix'}
                title={'Lifecycle Metrics'}
                containerStyles={{ width: '100%' }}
                disableSearch={true}
                disableDownloads={true}
                disableFiltersSettings={true}
                disableColumnSettings={true}
                showPrimaryButton={false}
                tableColumns={tableColumns}
                tableData={tableData}
                isLoading={false}
                disablePagination={true}
                emptyMessage="No data found"
                customRightActions={true}
                customRightActionsView={customRightActionsView}
            />
        </TableContainer>
    );
};

export default BatteryLifecycleMetrics;
